/**
 * Finance Redux Sagas
 *
 * Handles async operations for finance domain.
 * Balance is CALCULATED from transactions on the backend.
 * Related: docs/engineering/CHARGE_SYSTEM.md
 */

import { call, put, takeLatest } from '@spcc/core'
import type { PayloadAction } from '@reduxjs/toolkit'
import { financeActions } from './finance.slice'
import { balanceApi, financeApi, invoiceApi, zatcaInvoiceApi } from '../infrastructure/api/finance-api'
import type { BalanceSummary, BalanceCheckResult, FinanceFilter, PaymentFilter, FinanceStats, Statement, Transaction, PaymentsResponse, CreatePaymentPayload, Payment, Invoice, InvoiceFilter, InvoicesResponse, SapBalances, ZatcaInvoice, ZatcaInvoicesResponse } from '../domain/types'

/**
 * Fetch customer balance summary.
 * Balance is calculated from transactions on the backend.
 */
function* fetchBalanceSaga() {
  try {
    const balance: BalanceSummary = yield call(balanceApi.getBalance)
    yield put(financeActions.fetchBalanceSuccess(balance))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch balance'
    yield put(financeActions.fetchBalanceFailure(errorMessage))
  }
}

/**
 * Check if customer has sufficient balance for an amount.
 * Used before placing orders to validate balance.
 */
function* checkBalanceSaga(action: PayloadAction<number>) {
  try {
    const result: BalanceCheckResult = yield call(balanceApi.checkBalance, action.payload)
    yield put(financeActions.checkBalanceSuccess(result))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to check balance'
    yield put(financeActions.checkBalanceFailure(errorMessage))
  }
}

/**
 * Fetch transactions list with filtering and pagination.
 */
function* fetchTransactionsSaga(action: PayloadAction<{ filter?: FinanceFilter; page?: number; pageSize?: number }>) {
  try {
    const { filter, page = 1, pageSize = 25 } = action.payload
    const result: { transactions: Transaction[]; meta: { page: number; pageSize: number; pageCount: number; total: number } } = yield call(
      financeApi.getTransactions,
      filter,
      page,
      pageSize
    )
    yield put(financeActions.fetchTransactionsSuccess(result))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch transactions'
    yield put(financeActions.fetchTransactionsFailure(errorMessage))
  }
}

/**
 * Create a new payment. Backend lifecycle triggers outbound SAP sync.
 */
function* createPaymentSaga(action: PayloadAction<CreatePaymentPayload>) {
  try {
    const created: Payment = yield call(financeApi.createPayment, action.payload)
    yield put(financeActions.createPaymentSuccess())
    // Reload payments list to show the new payment
    yield put(financeActions.fetchPayments({ page: 1, pageSize: 20 }))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to create payment'
    yield put(financeActions.createPaymentFailure(errorMessage))
  }
}

/**
 * Sync payments from SAP on-demand, then reload the list.
 */
function* syncPaymentsSaga() {
  try {
    yield call(financeApi.syncPayments)
    yield put(financeActions.syncPaymentsSuccess())
  } catch {
    yield put(financeActions.syncPaymentsFailure('Sync failed'))
  }
  // Always reload payments after sync attempt
  yield put(financeActions.fetchPayments({ page: 1, pageSize: 20 }))
}

/**
 * Fetch paginated payments list with filtering.
 */
function* fetchPaymentsSaga(action: PayloadAction<{ filter?: PaymentFilter; page?: number; pageSize?: number; sort?: string }>) {
  try {
    const { filter, page = 1, pageSize = 25, sort } = action.payload
    const result: PaymentsResponse = yield call(
      financeApi.getPayments,
      filter,
      page,
      pageSize,
      sort
    )
    yield put(financeActions.fetchPaymentsSuccess(result))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch payments'
    yield put(financeActions.fetchPaymentsFailure(errorMessage))
  }
}

/**
 * Fetch financial statistics for dashboard.
 */
function* fetchStatsSaga() {
  try {
    const stats: FinanceStats = yield call(financeApi.getFinanceStats)
    yield put(financeActions.fetchStatsSuccess(stats))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch stats'
    yield put(financeActions.fetchStatsFailure(errorMessage))
  }
}

/**
 * Fetch Statement of Account for a date range.
 */
function* fetchStatementSaga(action: PayloadAction<{ from: string; to: string }>) {
  try {
    const { from, to } = action.payload
    const statement: Statement = yield call(financeApi.getStatement, from, to)
    yield put(financeActions.fetchStatementSuccess(statement))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch statement'
    yield put(financeActions.fetchStatementFailure(errorMessage))
  }
}

function* syncInvoicesSaga() {
  try {
    yield call(invoiceApi.syncInvoices)
    yield put(financeActions.syncInvoicesSuccess())
  } catch {
    yield put(financeActions.syncInvoicesFailure('Sync failed'))
  }
  // Always reload invoices after sync attempt
  yield put(financeActions.fetchInvoices({ page: 1, pageSize: 20 }))
}

function* fetchInvoicesSaga(action: PayloadAction<{ filter?: InvoiceFilter; page?: number; pageSize?: number; sort?: string }>) {
  try {
    const { filter, page = 1, pageSize = 20, sort } = action.payload
    const result: InvoicesResponse = yield call(
      invoiceApi.getInvoices,
      filter,
      page,
      pageSize,
      sort
    )
    yield put(financeActions.fetchInvoicesSuccess(result))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch invoices'
    yield put(financeActions.fetchInvoicesFailure(errorMessage))
  }
}

function* fetchInvoiceDetailSaga(action: PayloadAction<string>) {
  try {
    const invoice: Invoice = yield call(invoiceApi.getInvoice, action.payload)
    yield put(financeActions.fetchInvoiceDetailSuccess(invoice))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch invoice detail'
    yield put(financeActions.fetchInvoiceDetailFailure(errorMessage))
  }
}

function* fetchZatcaInvoicesSaga(action: PayloadAction<{ filter?: InvoiceFilter; page?: number; pageSize?: number; sort?: string }>) {
  try {
    const { filter, page = 1, pageSize = 20, sort } = action.payload
    const result: ZatcaInvoicesResponse = yield call(
      zatcaInvoiceApi.getInvoices,
      filter,
      page,
      pageSize,
      sort
    )
    yield put(financeActions.fetchZatcaInvoicesSuccess(result))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch ZATCA invoices'
    yield put(financeActions.fetchZatcaInvoicesFailure(errorMessage))
  }
}

function* fetchZatcaInvoiceDetailSaga(action: PayloadAction<string>) {
  try {
    const invoice: ZatcaInvoice = yield call(zatcaInvoiceApi.getInvoice, action.payload)
    yield put(financeActions.fetchZatcaInvoiceDetailSuccess(invoice))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch ZATCA invoice detail'
    yield put(financeActions.fetchZatcaInvoiceDetailFailure(errorMessage))
  }
}

/**
 * Fetch SAP balances on-demand from CPI (finance + contract).
 */
function* fetchSapBalancesSaga() {
  try {
    const result: SapBalances = yield call(balanceApi.getSapBalances)
    yield put(financeActions.fetchSapBalancesSuccess(result))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch SAP balances'
    yield put(financeActions.fetchSapBalancesFailure(errorMessage))
  }
}

/**
 * Fetch cached SAP balances from DB (sub-100ms).
 * Only populates state if cached data has a syncedAt timestamp.
 */
function* fetchSapBalancesCachedSaga() {
  try {
    const result: SapBalances = yield call(balanceApi.getSapBalancesCached)
    if (result.syncedAt) {
      yield put(financeActions.fetchSapBalancesSuccess(result))
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch cached SAP balances'
    yield put(financeActions.fetchSapBalancesFailure(errorMessage))
  }
}

/**
 * Background refresh of SAP balances from CPI (stale-while-revalidate).
 * On failure, existing cached data stays visible.
 */
function* refreshSapBalancesSaga() {
  try {
    const result: SapBalances = yield call(balanceApi.getSapBalances)
    yield put(financeActions.refreshSapBalancesSuccess(result))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to refresh SAP balances'
    yield put(financeActions.refreshSapBalancesFailure(errorMessage))
  }
}

/**
 * Retry outbound SAP sync for a failed portal-originated payment.
 */
function* retryPaymentSyncSaga(action: PayloadAction<string>) {
  try {
    yield call(financeApi.syncPaymentOutbound, action.payload)
    yield put(financeActions.retryPaymentSyncSuccess())
    yield put(financeActions.fetchPayments({ page: 1, pageSize: 20 }))
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Sync failed'
    yield put(financeActions.retryPaymentSyncFailure(errorMessage))
  }
}

export function* financeSaga() {
  yield takeLatest(financeActions.fetchBalance.type, fetchBalanceSaga)
  yield takeLatest(financeActions.checkBalance.type, checkBalanceSaga)
  yield takeLatest(financeActions.fetchTransactions.type, fetchTransactionsSaga)
  yield takeLatest(financeActions.createPayment.type, createPaymentSaga)
  yield takeLatest(financeActions.syncPayments.type, syncPaymentsSaga)
  yield takeLatest(financeActions.fetchPayments.type, fetchPaymentsSaga)
  yield takeLatest(financeActions.fetchStats.type, fetchStatsSaga)
  yield takeLatest(financeActions.fetchStatement.type, fetchStatementSaga)
  yield takeLatest(financeActions.syncInvoices.type, syncInvoicesSaga)
  yield takeLatest(financeActions.fetchInvoices.type, fetchInvoicesSaga)
  yield takeLatest(financeActions.fetchInvoiceDetail.type, fetchInvoiceDetailSaga)
  yield takeLatest(financeActions.fetchZatcaInvoices.type, fetchZatcaInvoicesSaga)
  yield takeLatest(financeActions.fetchZatcaInvoiceDetail.type, fetchZatcaInvoiceDetailSaga)
  yield takeLatest(financeActions.fetchSapBalances.type, fetchSapBalancesSaga)
  yield takeLatest(financeActions.fetchSapBalancesCached.type, fetchSapBalancesCachedSaga)
  yield takeLatest(financeActions.refreshSapBalances.type, refreshSapBalancesSaga)
  yield takeLatest(financeActions.retryPaymentSync.type, retryPaymentSyncSaga)
}
